import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { INCOMING_MATCH_STATUS_LABELS } from '@/types/bank-reconciliation';
import type { FundingChain, FundingStep } from '@/types/currency-conversion';
import {
  formatCurrency,
  formatDate,
  safeParseNumber,
} from '@/utils/formatters';

import { FundingStepDetails } from './FundingStepDetails';

// The two trade legs share a group: the rate only means something with both of
// them in view.
const GROUPS: { label: string; roles: FundingStep['role'][] }[] = [
  { label: 'Money out', roles: ['FUNDING_OUT'] },
  { label: 'Money in', roles: ['FUNDING_IN'] },
  { label: 'Ending FX value', roles: ['DISPOSED', 'ACQUIRED'] },
];

export interface ConversionFundingChainProps {
  fundId: string;
  chain: FundingChain;
}

/**
 * Provenance, not verification: one line settles the conversion, the rest are
 * the same money moving between the fund's own accounts. So they sit below the
 * evidence, outside the coverage it sums.
 *
 * The caller owns the fetch and renders this only once there is a chain.
 */
export function ConversionFundingChain({
  fundId,
  chain,
}: ConversionFundingChainProps) {
  const [opened, setOpened] = useState<FundingStep | null>(null);

  return (
    <div className="mt-3">
      <TieOut chain={chain} />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Account</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {GROUPS.map(({ label, roles }) => {
            const steps = chain.steps.filter((step) =>
              roles.includes(step.role)
            );
            if (steps.length === 0) return null;

            return (
              <GroupRows
                key={label}
                label={label}
                steps={steps}
                onOpen={setOpened}
              />
            );
          })}
        </TableBody>
      </Table>

      <FundingStepDetails
        fundId={fundId}
        step={opened}
        onClose={() => setOpened(null)}
      />
    </div>
  );
}

function GroupRows({
  label,
  steps,
  onOpen,
}: {
  label: string;
  steps: FundingStep[];
  onOpen: (step: FundingStep) => void;
}) {
  return (
    <>
      <TableRow className="hover:bg-transparent">
        <TableCell
          colSpan={2}
          className="pt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
        >
          {label}
        </TableCell>
        <TableCell className="pt-5 text-right text-xs font-medium tabular-nums whitespace-nowrap">
          {netByCurrency(steps).join(' · ')}
        </TableCell>
        <TableCell colSpan={2} className="pt-5" />
      </TableRow>
      {steps.map((step) => (
        <TableRow
          key={step.incoming_id}
          className="cursor-pointer"
          onClick={() => onOpen(step)}
        >
          <TableCell className="whitespace-nowrap">
            {step.value_date ? formatDate(step.value_date) : '-'}
          </TableCell>
          <TableCell className="whitespace-nowrap font-medium">
            {step.account_name}
          </TableCell>
          <TableCell className="text-right tabular-nums whitespace-nowrap">
            {formatCurrency(step.amount, { currency: step.currency })}
          </TableCell>
          <TableCell className="whitespace-nowrap">
            <Badge
              variant={step.is_evidence ? 'default' : 'secondary'}
              className="font-normal"
            >
              {step.is_evidence
                ? 'Evidence'
                : INCOMING_MATCH_STATUS_LABELS[step.match_status]}
            </Badge>
          </TableCell>
          <TableCell className="text-muted-foreground">
            {step.description ?? '-'}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/** Totalled in cents, because float addition of the amounts is not exact. */
function netByCurrency(steps: FundingStep[]): string[] {
  const cents = new Map<string, number>();
  for (const step of steps) {
    const amount = Math.round(safeParseNumber(step.amount) * 100);
    cents.set(step.currency, (cents.get(step.currency) ?? 0) + amount);
  }
  return [...cents].map(([currency, total]) =>
    formatCurrency(total / 100, { currency })
  );
}

/**
 * Net movement, because the hops cancel against each other. Only the sides that
 * are out are named.
 *
 * Never a reason to distrust the conversion: the funding-side reference is text
 * an operator typed, so a hop can go missing with nothing wrong with the trade.
 */
function TieOut({ chain }: { chain: FundingChain }) {
  const sides = [
    {
      currency: chain.evidence_currency,
      movement: chain.evidence_movement,
      expected: chain.evidence_expected,
    },
    {
      currency: chain.foreign_currency,
      movement: chain.foreign_movement,
      expected: chain.foreign_expected,
    },
  ].filter(
    (side) => safeParseNumber(side.movement) !== safeParseNumber(side.expected)
  );

  if (sides.length === 0) return null;

  return (
    <div className="mb-3 space-y-1">
      {sides.map((side) => (
        <span
          key={side.currency}
          className="flex items-start gap-1.5 text-sm text-amber-600 dark:text-amber-400"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {side.currency} nets to{' '}
            <span className="tabular-nums">
              {formatCurrency(side.movement, { currency: side.currency })}
            </span>
            , not{' '}
            <span className="tabular-nums">
              {formatCurrency(side.expected, { currency: side.currency })}
            </span>{' '}
            - a hop is missing, or a stray line shares this reference.
          </span>
        </span>
      ))}
    </div>
  );
}
